/**
 * Port of `src/components/StructuralPipes.js` from the App Builder
 * export. Renders translucent Three.js-style tubes between each floor
 * level to suggest the building's structural grid, plus thin wall
 * panels between adjacent tubes, and blends a tint color into every
 * visible space.
 *
 * Exposes module-level `setPipeTintColor` / `setPipeTintAmount` /
 * `rebuildFloorPipes` / `clearFloorPipes` APIs. `initStructuralPipes`
 * must be called once after the store is available so we can subscribe
 * to map-view / stacking changes.
 */

import { interpolateColor } from '../lib/map-helpers';
import type { MappedinFloor, MappedinMapView } from '../lib/mappedin-types';
import { useAppStore } from '../state/app-store';

const SP_COLOR = '#5eead4';
const SP_OPACITY = 0.02;
const SP_RADIUS_M = 0.275;
const WALL_COLOR = '#5eead4';
const WALL_OPACITY = 0.01;
const WALL_THICKNESS_M = 0.15;
const SP_STEPS = 10;
const DARK_THRESHOLD = 100;
const SECTOR_DEG = 8;

let spShapes: unknown[] = [];
let spKey = '';
const tintedSpaces: Array<{ sp: unknown; orig: string | null }> = [];
// True once `applyTint` has finished a full pass for the current `mapView`.
// Reset on map switch or when an explicit `rebuildFloorPipes(true)` requests
// fresh tints (e.g. tint color / amount changed). Lets `spBuild` skip the
// hundreds of redundant `updateState({ color })` calls that would otherwise
// run every time the stacking key changes — those calls were the reason the
// rebuild had to be debounced behind the 600ms altitude tween.
let tintsApplied = false;

let pipeTintColor = '#026b7e';
let pipeTintAmount = 0.25;

let rebuildTimer: ReturnType<typeof setTimeout> | null = null;
let prevMapView: MappedinMapView | null = null;
let initialized = false;

export function getPipeTintColor(): string {
  return pipeTintColor;
}
export function getPipeTintAmount(): number {
  return pipeTintAmount;
}
export function setPipeTintColor(value: string): void {
  pipeTintColor = value;
}
export function setPipeTintAmount(value: number): void {
  pipeTintAmount = value;
}

function luminance(hex: string): number {
  if (!hex || typeof hex !== 'string') return 255;
  let h = hex.replace('#', '');
  if (h.length === 3) {
    const h0 = h[0] ?? '';
    const h1 = h[1] ?? '';
    const h2 = h[2] ?? '';
    h = h0 + h0 + h1 + h1 + h2 + h2;
  }
  if (h.length !== 6) return 255;
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return 255;
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

interface Point {
  lat: number;
  lng: number;
}

function spCircle(lat: number, lng: number): unknown {
  const r = SP_RADIUS_M / 111320;
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const coords: number[][] = [];
  for (let i = 0; i <= SP_STEPS; i++) {
    const a = (2 * Math.PI * i) / SP_STEPS;
    coords.push([lng + (r * Math.cos(a)) / cosLat, lat + r * Math.sin(a)]);
  }
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: { type: 'Polygon', coordinates: [coords] },
      },
    ],
  };
}

function findPerimeterCorners(points: Point[]): Point[] {
  if (points.length < 3) return points.slice();
  let cLat = 0;
  let cLng = 0;
  for (const p of points) {
    cLat += p.lat;
    cLng += p.lng;
  }
  cLat /= points.length;
  cLng /= points.length;
  const cosC = Math.cos((cLat * Math.PI) / 180);
  const nSectors = Math.ceil(360 / SECTOR_DEG);
  const buckets: Array<{ lat: number; lng: number; dist: number } | undefined> = new Array(
    nSectors,
  );
  for (const p of points) {
    const dy = (p.lat - cLat) * 111320;
    const dx = (p.lng - cLng) * 111320 * cosC;
    let angle = (Math.atan2(dy, dx) * 180) / Math.PI;
    if (angle < 0) angle += 360;
    const sector = Math.floor(angle / SECTOR_DEG) % nSectors;
    const dist = dx * dx + dy * dy;
    const existing = buckets[sector];
    if (!existing || dist > existing.dist) {
      buckets[sector] = { lat: p.lat, lng: p.lng, dist };
    }
  }
  const outline: Point[] = [];
  for (const b of buckets) {
    if (b) outline.push({ lat: b.lat, lng: b.lng });
  }
  if (outline.length < 3) return outline;
  const MIN_TURN = 12;
  const corners: Point[] = [];
  const n = outline.length;
  for (let m = 0; m < n; m++) {
    const prev = outline[(m - 1 + n) % n];
    const cur = outline[m];
    const next = outline[(m + 1) % n];
    if (!prev || !cur || !next) continue;
    const ax = (cur.lng - prev.lng) * cosC;
    const ay = cur.lat - prev.lat;
    const bx = (next.lng - cur.lng) * cosC;
    const by = next.lat - cur.lat;
    const d2 = ax * bx + ay * by;
    const cross = ax * by - ay * bx;
    const turn = Math.abs((Math.atan2(cross, d2) * 180) / Math.PI);
    if (turn > MIN_TURN) corners.push(cur);
  }
  return corners.length >= 3 ? corners : outline;
}

interface GeoLike {
  type?: string;
  coordinates?: unknown;
  geometry?: GeoLike | null;
  features?: GeoLike[];
}

type NumRing = number[][];
type NumPolygon = NumRing[];

function isNumRing(v: unknown): v is NumRing {
  if (!Array.isArray(v)) return false;
  for (const coord of v) {
    if (!Array.isArray(coord) || coord.length < 2) return false;
    if (typeof coord[0] !== 'number' || typeof coord[1] !== 'number') return false;
  }
  return true;
}

function isNumPolygon(v: unknown): v is NumPolygon {
  if (!Array.isArray(v)) return false;
  for (const ring of v) if (!isNumRing(ring)) return false;
  return true;
}

/**
 * Walks any GeoJSON-like structure (Polygon / MultiPolygon / Feature /
 * FeatureCollection) and invokes `onPolygon` for every concrete polygon
 * it contains. Accepts `unknown` so callers don't need to cast.
 */
function forEachPolygon(geo: unknown, onPolygon: (poly: NumPolygon) => void): void {
  if (!geo || typeof geo !== 'object') return;
  const g = geo as GeoLike;
  if (g.type === 'Polygon') {
    if (isNumPolygon(g.coordinates)) onPolygon(g.coordinates);
    return;
  }
  if (g.type === 'MultiPolygon') {
    if (Array.isArray(g.coordinates)) {
      for (const poly of g.coordinates) {
        if (isNumPolygon(poly)) onPolygon(poly);
      }
    }
    return;
  }
  if (g.type === 'Feature' && g.geometry) {
    forEachPolygon(g.geometry, onPolygon);
    return;
  }
  if (g.type === 'FeatureCollection' && Array.isArray(g.features)) {
    for (const feat of g.features) forEachPolygon(feat, onPolygon);
  }
}

function extractGeoJSONPts(geo: unknown): Point[] {
  const pts: Point[] = [];
  forEachPolygon(geo, (poly) => {
    for (const ring of poly) {
      for (const coord of ring) {
        const lng = coord[0];
        const lat = coord[1];
        if (
          typeof lng === 'number' &&
          typeof lat === 'number' &&
          Number.isFinite(lng) &&
          Number.isFinite(lat)
        ) {
          pts.push({ lat, lng });
        }
      }
    }
  });
  return pts;
}

interface FloorWithSpaces extends MappedinFloor {
  spaces?: Array<{
    id: string;
    name?: string;
    geoJSON?: unknown;
    center?: { latitude: number; longitude: number };
  }>;
  maxHeight?: number;
}

function getFloorPoints(floors: FloorWithSpaces[]): Record<string, Point[]> {
  const hidSet = useAppStore.getState().hiddenSpaceIds;
  const floorPts: Record<string, Point[]> = {};
  for (const fl of floors) {
    const bucket: Point[] = [];
    floorPts[fl.id] = bucket;
    const spaces = fl.spaces ?? [];
    for (const sp of spaces) {
      if (hidSet.has(sp.id)) continue;
      const pts = extractGeoJSONPts(sp.geoJSON);
      if (pts.length >= 2) {
        for (const p of pts) bucket.push(p);
      } else if (sp.center) {
        bucket.push({ lat: sp.center.latitude, lng: sp.center.longitude });
      }
    }
  }
  return floorPts;
}

function pointInRing(lat: number, lng: number, ring: NumRing): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const pi = ring[i];
    const pj = ring[j];
    if (!pi || !pj) continue;
    const xi = pi[0];
    const yi = pi[1];
    const xj = pj[0];
    const yj = pj[1];
    if (
      typeof xi !== 'number' ||
      typeof yi !== 'number' ||
      typeof xj !== 'number' ||
      typeof yj !== 'number'
    ) {
      continue;
    }
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function hitsFloorGeometry(lat: number, lng: number, floorPolys: NumPolygon[]): boolean {
  for (const poly of floorPolys) {
    const outer = poly[0];
    if (outer && pointInRing(lat, lng, outer)) return true;
  }
  return false;
}

function getFloorPolygons(floor: FloorWithSpaces): NumPolygon[] {
  const hidSet = useAppStore.getState().hiddenSpaceIds;
  const polys: NumPolygon[] = [];
  const spaces = floor.spaces ?? [];
  for (const sp of spaces) {
    if (hidSet.has(sp.id)) continue;
    forEachPolygon(sp.geoJSON, (poly) => polys.push(poly));
  }
  return polys;
}

function spClear(): void {
  const mv = useAppStore.getState().mapView;
  if (mv?.Shapes) {
    for (const shape of spShapes) {
      try {
        mv.Shapes.remove(shape);
      } catch {
        // Already removed.
      }
    }
  }
  spShapes = [];
}

/**
 * Restore previous tints only for spaces still known to this mapView —
 * entries from prior venues would fire "No space found" warnings.
 */
function restoreTints(mapView: MappedinMapView): void {
  for (const entry of tintedSpaces) {
    if (!mapView.getState?.(entry.sp)) continue;
    try {
      mapView.updateState(entry.sp, { color: entry.orig });
    } catch {
      // Ignore.
    }
  }
  tintedSpaces.length = 0;
}

interface TintableSpace {
  id: string;
  name?: string;
}

function tintOneSpace(mapView: MappedinMapView, sp: TintableSpace): void {
  try {
    const origState = mapView.getState?.(sp);
    if (!origState) return;
    const origColor = origState.color ?? '#ffffff';
    const name = sp.name ?? '';
    const isProperSpace = name !== '' && name !== 'room' && name !== 'space';
    if (isProperSpace && luminance(origColor) >= DARK_THRESHOLD) return;
    const blended = interpolateColor(origColor, pipeTintColor || '#1e2938', pipeTintAmount);
    mapView.updateState(sp, { color: blended });
    tintedSpaces.push({ sp, orig: origColor });
  } catch {
    // Ignore — one bad space shouldn't abort the tint pass.
  }
}

function applyTint(): void {
  const store = useAppStore.getState();
  const { floors, mapView } = store;
  if (!mapView || !floors.length) return;
  restoreTints(mapView);
  const hidSet = store.hiddenSpaceIds;
  for (const fl of floors as FloorWithSpaces[]) {
    const spaces = fl.spaces ?? [];
    for (const sp of spaces) {
      if (hidSet.has(sp.id)) continue;
      tintOneSpace(mapView, sp);
    }
  }
  tintsApplied = true;
}

interface ElevationPlan {
  uElev: number[];
  eOrd: Record<number, number>;
}

function computeElevationPlan(sorted: FloorWithSpaces[]): ElevationPlan | null {
  const uElev: number[] = [];
  const eSeen: Record<number, boolean> = {};
  for (const f of sorted) {
    const e = f.elevation ?? 0;
    if (!eSeen[e]) {
      eSeen[e] = true;
      uElev.push(e);
    }
  }
  uElev.sort((a, b) => a - b);
  if (uElev.length < 2) return null;
  const eOrd: Record<number, number> = {};
  uElev.forEach((e, i) => {
    eOrd[e] = i;
  });
  return { uElev, eOrd };
}

interface FloorPair {
  bottom: FloorWithSpaces;
  top: FloorWithSpaces;
  pipeHeight: number;
}

function findFloorAt(sorted: FloorWithSpaces[], elev: number): FloorWithSpaces | null {
  for (const f of sorted) if ((f.elevation ?? 0) === elev) return f;
  return null;
}

function computeFloorPair(
  sorted: FloorWithSpaces[],
  plan: ElevationPlan,
  index: number,
  floorGap: number,
): FloorPair | null {
  const bottomElev = plan.uElev[index];
  const topElev = plan.uElev[index + 1];
  if (typeof bottomElev !== 'number' || typeof topElev !== 'number') return null;
  const bottomOrd = plan.eOrd[bottomElev];
  const topOrd = plan.eOrd[topElev];
  if (typeof bottomOrd !== 'number' || typeof topOrd !== 'number') return null;
  const top = findFloorAt(sorted, topElev);
  const bottom = findFloorAt(sorted, bottomElev);
  if (!bottom || !top) return null;
  const slabHeight = top.maxHeight ?? 2;
  const pipeHeight = (topOrd - bottomOrd) * floorGap + slabHeight;
  if (pipeHeight <= 0) return null;
  return { bottom, top, pipeHeight };
}

function addPipeShape(
  mapView: MappedinMapView,
  pos: Point,
  pipeHeight: number,
  bottomFloor: FloorWithSpaces,
): void {
  if (!mapView.Shapes) return;
  try {
    spShapes.push(
      mapView.Shapes.add(
        spCircle(pos.lat, pos.lng),
        {
          color: SP_COLOR,
          altitude: 0,
          height: pipeHeight,
          opacity: SP_OPACITY,
          interactive: false,
        },
        bottomFloor,
      ),
    );
  } catch {
    // Ignore.
  }
}

function keyOfPoint(lat: number, lng: number): string {
  return `${Math.round(lat * 100000)},${Math.round(lng * 100000)}`;
}

function placePipesForCorners(
  mapView: MappedinMapView,
  corners: Point[],
  counterpartPolys: NumPolygon[],
  pipeHeight: number,
  bottomFloor: FloorWithSpaces,
  placed: Record<string, boolean>,
  positions: Point[],
): void {
  for (const c of corners) {
    if (!hitsFloorGeometry(c.lat, c.lng, counterpartPolys)) continue;
    const k = keyOfPoint(c.lat, c.lng);
    if (placed[k]) continue;
    placed[k] = true;
    positions.push(c);
    addPipeShape(mapView, c, pipeHeight, bottomFloor);
  }
}

function computeCentroid(positions: Point[]): Point {
  let cxLat = 0;
  let cxLng = 0;
  for (const p of positions) {
    cxLat += p.lat;
    cxLng += p.lng;
  }
  const n = positions.length || 1;
  return { lat: cxLat / n, lng: cxLng / n };
}

function addWallShape(
  mapView: MappedinMapView,
  wA: Point,
  wB: Point,
  pipeHeight: number,
  bottomFloor: FloorWithSpaces,
  cosW: number,
): void {
  if (!mapView.Shapes) return;
  const dLat = wB.lat - wA.lat;
  const dLng = wB.lng - wA.lng;
  const meterLat = dLat * 111320;
  const meterLng = dLng * 111320 * cosW;
  const len = Math.sqrt(meterLat * meterLat + meterLng * meterLng);
  if (len < 1 || len > 80) return;

  let pLat = -dLng * cosW;
  let pLng = dLat / cosW;
  const pLen = Math.sqrt(pLat * pLat + pLng * pLng);
  if (pLen < 1e-10) return;
  const wallThickDeg = WALL_THICKNESS_M / 111320;
  pLat = (pLat / pLen) * wallThickDeg;
  pLng = (pLng / pLen) * wallThickDeg;

  const wallCoords = [
    [wA.lng - pLng, wA.lat - pLat],
    [wA.lng + pLng, wA.lat + pLat],
    [wB.lng + pLng, wB.lat + pLat],
    [wB.lng - pLng, wB.lat - pLat],
    [wA.lng - pLng, wA.lat - pLat],
  ];
  const wallGeo = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: { type: 'Polygon', coordinates: [wallCoords] },
      },
    ],
  };
  try {
    spShapes.push(
      mapView.Shapes.add(
        wallGeo,
        {
          color: WALL_COLOR,
          altitude: 0,
          height: pipeHeight,
          opacity: WALL_OPACITY,
          interactive: false,
        },
        bottomFloor,
      ),
    );
  } catch {
    // Ignore.
  }
}

function placeWalls(
  mapView: MappedinMapView,
  positions: Point[],
  pipeHeight: number,
  bottomFloor: FloorWithSpaces,
): void {
  if (positions.length < 2) return;
  const centroid = computeCentroid(positions);
  positions.sort((a, b) => {
    const aa = Math.atan2(a.lat - centroid.lat, a.lng - centroid.lng);
    const ba = Math.atan2(b.lat - centroid.lat, b.lng - centroid.lng);
    return aa - ba;
  });
  const cosW = Math.cos((centroid.lat * Math.PI) / 180);
  const n = positions.length;
  for (let wi = 0; wi < n; wi++) {
    const wA = positions[wi];
    const wB = positions[(wi + 1) % n];
    if (!wA || !wB) continue;
    addWallShape(mapView, wA, wB, pipeHeight, bottomFloor, cosW);
  }
}

function buildPairPipes(
  mapView: MappedinMapView,
  pair: FloorPair,
  floorSamples: Record<string, Point[]>,
): void {
  const { bottom, top, pipeHeight } = pair;
  const topCorners = floorSamples[top.id] ?? [];
  const bottomCorners = floorSamples[bottom.id] ?? [];
  const lowerPolys = getFloorPolygons(bottom);
  const upperPolys = getFloorPolygons(top);
  const positions: Point[] = [];
  const placed: Record<string, boolean> = {};
  placePipesForCorners(mapView, topCorners, lowerPolys, pipeHeight, bottom, placed, positions);
  placePipesForCorners(mapView, bottomCorners, upperPolys, pipeHeight, bottom, placed, positions);
  placeWalls(mapView, positions, pipeHeight, bottom);
}

function spBuild(): void {
  spClear();
  // Only re-tint when we don't already have a current tint pass for this
  // mapView. The first `rebuildFloorPipes(true)` from `processLoadedMap`
  // applies tints synchronously before the stack animation; subsequent
  // pipe rebuilds (stack toggle, gap change, etc.) just need to add the
  // shapes. Skipping the redundant tint pass is what lets us drop the
  // 900ms debounce that previously held pipes off-screen.
  if (!tintsApplied) applyTint();
  const store = useAppStore.getState();
  const { mapView, mapData, isStacked, floorGap } = store;
  const floors = store.floors as FloorWithSpaces[];
  if (!isStacked || !mapView || !mapData || floors.length < 2) return;
  if (!mapView.Shapes) return;

  const outdoorIds: Record<string, boolean> = {};
  for (const od of store.outdoorFloors) outdoorIds[od.id] = true;
  const visible = floors.filter((f) => !outdoorIds[f.id]);
  if (visible.length < 2) return;

  const sorted = visible.slice().sort((a, b) => (a.elevation ?? 0) - (b.elevation ?? 0));
  const plan = computeElevationPlan(sorted);
  if (!plan) return;

  const floorPts = getFloorPoints(visible);
  const floorSamples: Record<string, Point[]> = {};
  for (const fid of Object.keys(floorPts)) {
    floorSamples[fid] = findPerimeterCorners(floorPts[fid] ?? []);
  }

  for (let ei = 0; ei < plan.uElev.length - 1; ei++) {
    const pair = computeFloorPair(sorted, plan, ei, floorGap);
    if (!pair) continue;
    buildPairPipes(mapView, pair, floorSamples);
  }
}

function spCheck(): void {
  const store = useAppStore.getState();
  if (!store.mapView || !store.mapData) return;
  const key = `${store.isStacked ? '1' : '0'}:${store.floorGap}:${store.floors.length}`;
  if (key === spKey) return;
  spKey = key;
  if (!store.isStacked || store.floors.length < 2) {
    spClear();
    applyTint();
    return;
  }
  spBuild();
}

export function rebuildFloorPipes(force = false): void {
  if (force) {
    spKey = '';
    // `force` is the signal that something the tint pass depends on may
    // have changed (tint color / amount, hidden spaces). Drop the cache
    // so `spBuild` re-runs `applyTint`. Non-forced rebuilds (stack
    // toggle, gap change) preserve the cached tints.
    tintsApplied = false;
  }
  spCheck();
}

export function clearFloorPipes(): void {
  spClear();
}

/**
 * Subscribe to store changes so pipes rebuild when the stacking
 * configuration changes. Safe to call multiple times; only the first
 * call wires up the subscription.
 *
 * Notes on timing:
 * - On `mapView` change we reset module state but do NOT auto-schedule
 *   an `spCheck`. The initial tint + pipe build is driven explicitly
 *   from `VenueMap.processLoadedMap` via `rebuildFloorPipes(true)` so
 *   that it happens BEFORE the stack animation starts. Running the
 *   hundreds of `updateState(space, { color })` calls that `applyTint`
 *   emits in the middle of a 600ms altitude tween was causing the
 *   tween to get starved and the floor geometry to decouple from its
 *   markers.
 * - Subsequent store changes (stacking toggle, gap, labels, etc.) only
 *   need to add the pipe `Shapes` (a few dozen `Shapes.add` calls) —
 *   the per-space tint pass is gated behind `tintsApplied`, so it is
 *   skipped when nothing tint-relevant changed. That makes pipe rebuilds
 *   cheap enough to run with a short 100ms debounce instead of waiting
 *   the full 900ms+ for the altitude tween to finish.
 */
export function initStructuralPipes(): void {
  if (initialized) return;
  initialized = true;
  useAppStore.subscribe((state) => {
    if (state.mapView !== prevMapView) {
      prevMapView = state.mapView;
      spShapes = [];
      tintedSpaces.length = 0;
      tintsApplied = false;
      spKey = '';
      if (rebuildTimer) {
        clearTimeout(rebuildTimer);
        rebuildTimer = null;
      }
      return;
    }
    if (!state.mapView || !state.mapData || state.floors.length === 0) return;
    if (rebuildTimer) clearTimeout(rebuildTimer);
    rebuildTimer = setTimeout(spCheck, 100);
  });
}
